import React, { useState, useEffect, useCallback } from 'react';
import { Check, X, Clock, Loader2, ChevronRight, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// The dashboard's ONE approvals surface. Fed by GET /api/approvals — the federated
// aggregator (app/services/approvals.py). Every approval-shaped item across
// subsystems lands here: bookings, appointment requests, document shares, and any
// future provider, with no change to this card. Actions dispatch to the owning
// subsystem's own endpoints and clear live. (This card was previously bound to
// GET /documents/shares/pending only — that type is now a provider.)
const TYPE_BADGE = {
  booking_approval: { label: 'Booking', cls: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40' },
  appointment_request: { label: 'Appointment request', cls: 'bg-violet-500/20 text-violet-300 border-violet-500/40' },
  document_share: { label: 'Document share', cls: 'bg-blue-500/20 text-blue-300 border-blue-500/40' },
};

const ApprovalWidget = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/approvals');
      // B3: approvals are a queue — OLDEST first, age is debt.
      setItems((res.data?.items || []).slice().sort(
        (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)));
    } catch (e) {
      /* best-effort: keep prior items */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAction = async (item, action) => {
    if (action.navigate) { if (action.endpoint) navigate(action.endpoint); return; }
    // action.body carries the action's own parameters. Without this, two
    // actions hitting the same endpoint are indistinguishable — which is how
    // "Decline" and "Decline & offer other times" would collapse back into one.
    let body = { ...(action.body || {}) };
    if (action.reason) body.reason = window.prompt('Reason (optional):') || '';
    const k = `${item.type}:${item.id}:${action.key}`;
    setBusy(k);
    try {
      const path = (action.endpoint || '').replace(/^\/api/, ''); // api baseURL is /api
      if (action.method === 'PUT') await api.put(path, body);
      else await api.post(path, body);
      setItems((prev) => prev.filter((i) => !(i.type === item.type && i.id === item.id))); // clears live
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl p-6 border border-virgo-teal-700/30">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-amber-500/20 rounded-lg">
          <Clock className="w-5 h-5 text-amber-400" />
        </div>
        <h3 className="text-lg font-semibold text-white">Pending Approvals</h3>
        <button onClick={() => navigate('/review/parties-counsel')} className="ml-auto text-xs text-cyan-300 hover:text-cyan-200 underline-offset-2 hover:underline" title="Stage-1 parties & counsel proposals, one decision per client">Review parties &amp; counsel by client →</button>
        {items.length > 0 && (
          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium">
            {items.length}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="animate-spin text-virgo-teal-400" size={24} />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-6">
          <Check className="w-10 h-10 text-green-500 mx-auto mb-2" />
          <p className="text-virgo-teal-400 text-sm">No pending approvals</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
          {items.map((item) => {
            const badge = TYPE_BADGE[item.type] || { label: item.type_label, cls: 'bg-slate-500/20 text-slate-300 border-slate-500/40' };
            return (
              <div key={`${item.type}:${item.id}`} className="bg-virgo-navy-600/50 rounded-lg p-3 border border-virgo-teal-700/20">
                <button
                  className="w-full min-w-0 text-left mb-2"
                  onClick={() => item.evidence_link && navigate(item.evidence_link)}
                >
                  <span className={`inline-block text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded border mb-1 ${badge.cls}`}>
                    {badge.label}
                  </span>
                  <p className="text-white text-sm font-medium truncate">{item.title}</p>
                  <p className="text-virgo-teal-400 text-xs truncate">{item.summary}</p>
                </button>
                <div className="flex items-center gap-1.5 justify-end">
                  {(item.actions || []).map((action) => {
                    const k = `${item.type}:${item.id}:${action.key}`;
                    const base = 'px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 flex items-center gap-1';
                    const style = action.style === 'primary' ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                      : action.style === 'danger' ? 'bg-red-600/80 hover:bg-red-600 text-white'
                      : 'text-cyan-300 hover:text-cyan-200';
                    return (
                      <button
                        key={action.key}
                        disabled={busy === k}
                        onClick={(e) => { e.stopPropagation(); runAction(item, action); }}
                        className={`${base} ${style}`}
                      >
                        {busy === k ? <Loader2 className="animate-spin" size={13} />
                          : action.key === 'approve' ? <CheckCircle size={13} />
                          : action.key === 'decline' ? <X size={13} />
                          : action.style === 'link' ? <ChevronRight size={13} /> : null}
                        {action.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApprovalWidget;
